import React, { useState, FormEvent, useEffect, useCallback } from "react";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { useHistory, Link as RouterLink, useLocation } from "react-router-dom";
import { useAuthContext } from "../../../../context/auth.context";
import { useUserContext } from "../../../../context/user.context";
import { useHttpClientContext } from "../../../../context/http-client.context";
import Button from "../../../components/Button/Button";
import TextInput from "../../../components/Inputs/TextInput/TextInput";
import Footer from "../../../components/Footer/Footer";
import PageLoader from "../../../components/PageLoader/PageLoader";
import { useAppContext } from "../../../../context/app.context";
import environment, { EnvId } from "../../../../env";
import LoginForm from "./Forms/LoginForm";
import RegisterForm from "./Forms/RegisterForm";
import { FormControlErrors } from "../../../../hooks/form.hook";
import { LoginRequest, RegisterRequest } from "../../../../models/login-request.model";
import { Alert } from "@material-ui/lab";
import ResetForm from "./Forms/ResetForm";

const useStyles = makeStyles((theme) => ({
  page: {
    background: theme.palette.background.default,
    width: "100%",
    minHeight: "100%",
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
  },
  centerContainer: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: "100%",
    justifyContent: "center",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  login: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
  },
  logoContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(2)
  },
  logo: {
    // Matches the old wordmark + "A product of" + enzee stack's total on-screen
    // height (~56px wordmark + text line + ~32px enzee logo). Height is the fixed
    // axis and width follows the image's own aspect ratio -- fixing width instead
    // (or both) stretches/distorts this wider 3-row lockup.
    height: 130,
    width: 'auto',
    maxWidth: '100%',
  },
  learnMoreEnzee: {
    transform: 'translate(0, 20px)',
    fontSize: '1.3rem'
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginBottom: theme.spacing(2),
  },
}));

export type loginFormType = "login" | "register" | "reset";

function Login(props: { formType?: loginFormType }) {
  const formType = props.formType || "login";

  const location = useLocation<{ message?: string } | undefined>();
  // Carried here by LoginForm/VerificationCodeForm when the backend reports that neither the
  // lowercase nor the historical exact-typed-case Cognito account exists for the entered email
  // (the username case-sensitivity migration's ACCOUNT_NOT_FOUND signal) -- shown once, above
  // whichever form/step is currently active.
  const redirectMessage = location.state?.message;

  //const verifyCode = useMemo(() => location.pathname.endsWith("/verify"), [location.pathname]);

  const classes = useStyles();

  // Hides Forgot password / Sign up links on the LoginForm's step-2 (post-discovery)
  // screen when the discovered account is SSO-only. Step 1 (email entry) always shows them.
  const [hideAuthLinks, setHideAuthLinks] = useState(false);

  return (
    <div className={classes.page}>
      <Container className={classes.centerContainer} component="div" maxWidth="xs">
        <div className={classes.login}>
          <div className={classes.logoContainer}>
            <img src="/zapit-qa-enzee-lockup.svg" className={classes.logo} alt="ZapIT! QA - a product of enzee" />
          </div>
          <Typography component="h1" variant="h5" className={classes.header} color="textPrimary">
            {formType === "reset" ? "Reset Password" : formType === "register" ? "Join Us" : "Sign In"}
          </Typography>
          {redirectMessage && (
            <Alert severity="info" style={{ width: "100%", marginBottom: 16, boxSizing: "border-box" }}>
              {redirectMessage}
            </Alert>
          )}
          <div className={classes.form}>
            {formType === "reset" ? <ResetForm /> : formType === "register" ? <RegisterForm /> : <LoginForm onSsoOnlyChange={setHideAuthLinks} />}
          </div>
          {
            <Grid container justify={formType === "login" ? "space-between" : "center"}>
              {formType === "login" && !hideAuthLinks && (
                <Grid item>
                  <Link component={RouterLink} to={`/reset`} variant="body2">
                    Forgot password? Reset it
                  </Link>
                </Grid>
              )}
              <Grid item>
                {formType === "register" ? (
                  <Link component={RouterLink} to="/login" variant="body2">
                    {"Have an account? Sign in"}
                  </Link>
                ) : (
                  !hideAuthLinks && (
                    <Link component={RouterLink} to="/register" variant="body2">
                      {"Need an account? Sign up"}
                    </Link>
                  )
                )}
              </Grid>
            </Grid>
          }
          <Typography className={classes.learnMoreEnzee} color="textPrimary">
            Learn more about Enzee <Link target="_blank" href="http://www.enzeehealth.com">here</Link>
          </Typography>
          <Box mt={8}>
            <Footer env={environment.id === EnvId.Prod ? undefined : EnvId[environment.id].toLowerCase()} version={environment.version} />
          </Box>
        </div>
      </Container>
    </div>
  );
}

export default Login;
